import { db, transactWithRetry, batchTransact } from '@/lib/db'
import { id } from '@instantdb/react'
import type { Folder } from '@/types'

/**
 * 文件夹管理 Hook
 * 提供文件夹的增删改查功能
 */
export function useFolders(projectId: string) {
  // 查询项目下的所有文件夹
  const { data, isLoading, error } = db.useQuery({
    folders: {
      $: {
        where: { project_id: projectId },
      },
    },
  })

  const folders = (data?.folders || []) as Folder[]

  // 添加文件夹
  const addFolder = async (params: {
    name: string
    description?: string
  }) => {
    const folderId = id()
    await transactWithRetry(
      db.tx.folders[folderId].update({
        id: folderId,
        project_id: projectId,
        name: params.name,
        description: params.description || '',
        created_at: Date.now(),
      })
    )
    return folderId
  }

  // 更新文件夹
  const updateFolder = async (
    folderId: string,
    updates: Partial<Omit<Folder, 'id' | 'project_id' | 'created_at'>>
  ) => {
    await transactWithRetry(db.tx.folders[folderId].update(updates))
  }

  // 删除文件夹
  const deleteFolder = async (folderId: string) => {
    // 1. 找出该文件夹下的所有素材，并将其 folder_id 置空（或者由用户决定，这里默认置空）
    const res = await db.queryOnce({
      assets: {
        $: {
          where: { folder_id: folderId }
        }
      }
    })
    const assets = res.data?.assets

    const txns = [
      db.tx.folders[folderId].delete(),
      ...(assets || []).map((asset: any) => db.tx.assets[asset.id].update({ folder_id: null }))
    ]

    await batchTransact(txns)
  }

  return {
    folders,
    isLoading,
    error,
    addFolder,
    updateFolder,
    deleteFolder,
  }
}
